/**
 * The reload-safe proof of a bye (fix AA, 2026-10-06).
 *
 * `bye_response` (`reducers/tournament.ts`) stamps `tournament.bye` on the
 * LIVE 1:1 message only — it is volatile, gone after a reload/reconnect —
 * and `tournament_status_response` (the one message a (re)connecting client
 * gets) carries no per-player field at all
 * (`connectionHandler.js`: `{round, roundState, ongoing_games, tournamentId,
 * message}`). So a reload lands a bye player on `tournamentStatus.ts` row 10
 * ("you're not paired this round") with no way to tell a bye apart from a
 * late joiner or an odd player out.
 *
 * The ROUND'S OWN PAIRINGS are the only reload-safe proof: the tserver
 * returns a bye as a row with no `opponent` (`TournamentPairingDao.js`),
 * which the old `CCTournaments/components/games.tsx`'s own bye popup already
 * keys off (`isUserInPlayer && !item?.opponent`). This hook re-reads that
 * same signal once the round has started and calls `actions().proveBye`
 * when it finds my own null-opponent row.
 *
 * THIS IS THE BACKSTOP ONLY — the live path is `bye_response`; this hook
 * only ever fires when `tournament.bye` does NOT already cover the current
 * round (`byeRound !== round`), so a live bye never triggers a redundant
 * fetch.
 */
import {useEffect, useRef} from 'react';
import {useShallow} from 'zustand/react/shallow';

import {liveGameStore, useLiveGame} from '@/Store/liveGameStore';
import type {LiveGameState} from '@/Store/liveGameStore';

import {roundStartedFrom} from './status/tournamentStatus';

const selectProbeInput = (s: LiveGameState) => ({
  tournamentId: s.tournament.id,
  mode: s.tournament.mode,
  round: s.tournament.currentRound,
  byeRound: s.tournament.bye?.round ?? null,
  roundState: s.tournament.roundState,
  pairingGenerated: s.tournament.pairingGenerated,
  ongoingGames: s.tournament.ongoingGames,
  ownBoard: s.game.phase === 'starting' || s.game.phase === 'playing',
  me: s.session.me,
});

export function useByeProbe(): void {
  // `@/Hooks/*` is the eager-graph landmine (ARCHITECTURE §13 landmine 1) —
  // required INSIDE the hook body, the way `hooks/useAssistEffects.ts` does
  // for its own REST mutation hook; interop-safe for an ESM-in-bundle /
  // CJS-under-jest module either way.
  const useGetTournamentPairings =
    require('@/Hooks/TournamentsApi/useGetTournamentPairings').default ??
    require('@/Hooks/TournamentsApi/useGetTournamentPairings');

  const {
    tournamentId,
    mode,
    round,
    byeRound,
    roundState,
    pairingGenerated,
    ongoingGames,
    ownBoard,
    me,
  } = useLiveGame(useShallow(selectProbeInput));

  const {mutate: fetchPairings} = useGetTournamentPairings();
  // the mutation's own identity is not guaranteed stable across renders
  // (`hooks/useAssistEffects.ts`'s own note) — reached through a ref so the
  // effect below does not have to re-run (and re-fetch) on every render.
  const fetchRef = useRef(fetchPairings);
  fetchRef.current = fetchPairings;

  /** rounds already probed this mount, keyed below — never re-fetch one. */
  const probedKeys = useRef<Set<string>>(new Set());

  // S10.1's "has the round actually started" predicate, SHARED with
  // `tournamentStatus.ts` row 10 (not a second copy that could drift from
  // it) — a reload must not probe before a round exists.
  const roundStarted = roundStartedFrom({
    currentRound: round,
    pairingGenerated,
    roundState,
    ongoingGames,
  });
  const shouldProbe =
    !!tournamentId &&
    mode === 'swiss' &&
    (round ?? 0) >= 1 &&
    byeRound !== round &&
    !ownBoard &&
    roundStarted &&
    // fix AA review (2026-10-06): `session.me` is '' until the handshake
    // resolves it — a probe before then would send `userId: ''`, which
    // `TournamentService.getPairings` cannot resolve to any pairing row.
    me !== '';

  useEffect(() => {
    if (!shouldProbe || !tournamentId || round === null) {
      return;
    }
    const key = `${tournamentId}:${round}:${pairingGenerated ? 1 : 0}`;
    if (probedKeys.current.has(key)) {
      return;
    }
    probedKeys.current.add(key);

    const actions = () => liveGameStore.getState().actions;

    /**
     * The SAME "jump to my page" call `CCTournaments/components/games.tsx`'s
     * own search makes (L712-740): `userId` tells the server which PAGE
     * contains MY pairing row (`TournamentService.getPairings` resolves the
     * offset from it before paginating), so this returns my row regardless
     * of the tournament's size — `limit`/`offset` are the component's own
     * defaults, `ongoing: false` on purpose (an `ongoing` filter excludes a
     * SCORED/bye-less row, and a bye must still be found after the round
     * has otherwise finished).
     */
    fetchRef.current(
      {tournamentId, round, limit: 10, offset: 0, ongoing: false, userId: me},
      {
        onSuccess: (data: any) => {
          const rows = data?.data ?? data ?? [];
          // a row where I am the OPPONENT means I am paired, not byed; an
          // UNPAIRED row (the arbiter's own mark, `TournamentService
          // .getPairings`'s `unpaired: pair.is_unpaired ? pair : undefined`
          // — truthy/falsy, never literally `true`) is a different case
          // entirely — only a null-opponent row where I am the PLAYER is a
          // bye.
          const gotBye = rows.some(
            (r: any) =>
              String(r?.player?.cc_userid ?? '') === String(me) &&
              !r?.opponent &&
              !r?.unpaired,
          );
          if (gotBye) {
            actions().proveBye(round);
          }
        },
      },
    );
  }, [shouldProbe, tournamentId, round, pairingGenerated, me]);
}

export default useByeProbe;
